import { z } from 'zod'

// One place that reads process.env. Everything else imports from here, so a missing variable
// fails at startup with its name instead of surfacing as a confusing runtime error.
const serverSchema = z.object({
  DATABASE_URL: z
    .string()
    .min(1)
    .refine((v) => v.startsWith('postgres://') || v.startsWith('postgresql://'), {
      message: 'must be a postgres:// connection string',
    }),
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  // email: without a key nothing is sent and every message is logged instead, so tests and local
  // work never reach a real inbox
  RESEND_API_KEY: z.string().min(1).optional(),
  MAIL_FROM: z.string().min(3).optional(),
  FOUNDER_EMAIL: z.string().email().optional(),
  // optional push channel; the email path runs with or without it
  NTFY_TOPIC: z.string().min(1).optional(),
  NEXT_PUBLIC_SITE_URL: z.string().url().default('https://ensemblemusic.online'),
  // admin auth (S0-015). Optional so the public site boots without them; the auth entrypoint
  // refuses to start when they are missing, which is better than a half-configured login.
  BETTER_AUTH_SECRET: z.string().min(32).optional(),
  // comma-separated; the only addresses that may ever receive a magic link
  ADMIN_EMAIL_ALLOWLIST: z.string().min(3).optional(),
  // Vercel Cron sends it as a bearer token (S0-020); unset means the cron endpoint answers 401
  CRON_SECRET: z.string().min(16).optional(),
})

export type ServerEnv = z.infer<typeof serverSchema>

let cached: ServerEnv | undefined

export function serverEnv(): ServerEnv {
  if (cached) return cached
  const parsed = serverSchema.safeParse(process.env)
  if (!parsed.success) {
    const missing = parsed.error.issues
      .map((i) => `  ${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('\n')
    throw new Error(
      `Invalid environment. Fix these variables (see apps/next/.env.example):\n${missing}`
    )
  }
  cached = parsed.data
  return cached
}
